import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Bell, CheckCircle2, AlertTriangle, Send, Clock, Plus, ChevronRight, XCircle, FileCheck, Timer, AlertOctagon } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { StatCard, Card, Button, FrequencyBadge, LoadingSpinner, EmptyState, Badge } from '../components/UI';

export default function Dashboard() {
  const { user, canManageReports } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/reminders/stats'),
      api.get('/reminders?is_active=1'),
      api.get('/logs/recent'),
    ]).then(([s, r, l]) => {
      setStats(s.data);
      setReminders(r.data.slice(0, 6));
      setRecent(l.data.slice(0, 8));
    }).finally(() => setLoading(false));
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  if (loading) return <LoadingSpinner />;

  const complianceRate = stats?.submitted > 0 ? Math.round((stats.onTime / stats.submitted) * 100) : null;

  return (
    <div style={{ padding: '32px 36px', maxWidth: 1280, margin: '0 auto' }} className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: 32, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            {greeting}, {user?.full_name?.split(' ')[0] || user?.username} 👋
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13.5 }}>
            {format(new Date(), 'EEEE, MMMM d, yyyy')} · {user?.role} · {user?.department_name || 'All Departments'}
          </p>
        </div>
        {canManageReports && (
          <Button icon={Plus} onClick={() => navigate('/reminders/new')}>New Report</Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px,1fr))', gap: 14, marginBottom: 28 }}>
          <StatCard label="Total Reports" value={stats.total} icon={Bell} color="var(--primary)" />
          <StatCard label="Active" value={stats.active} icon={CheckCircle2} color="var(--success)" />
          <StatCard label="Expiring Soon" value={stats.expiringSoon} icon={AlertTriangle} color="var(--warning)" />
          <StatCard label="Submitted" value={stats.submitted} icon={FileCheck} color="var(--info)" />
          <StatCard label="On-Time Rate" value={complianceRate !== null ? `${complianceRate}%` : '—'} icon={Timer} color={complianceRate >= 80 ? 'var(--success)' : 'var(--danger)'} />
          <StatCard label="Overdue" value={stats.overdue} icon={AlertOctagon} color="var(--danger)" />
          <StatCard label="Sent Today" value={stats.sentToday} icon={Send} color="#06b6d4" />
          <StatCard label="Failed" value={stats.failedThisMonth} icon={XCircle} color="var(--danger)" />
        </div>
      )}

      {/* Compliance bar */}
      {complianceRate !== null && (
        <Card style={{ marginBottom: 24, padding: '18px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Overall Compliance Rate</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: complianceRate >= 80 ? 'var(--success)' : complianceRate >= 60 ? 'var(--warning)' : 'var(--danger)' }}>{complianceRate}%</span>
          </div>
          <div style={{ height: 10, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${complianceRate}%`, borderRadius: 99, background: complianceRate >= 80 ? 'var(--success)' : complianceRate >= 60 ? 'var(--warning)' : 'var(--danger)', transition: 'width 1s ease' }} />
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 12 }}>
            <span style={{ color: 'var(--success)', fontWeight: 600 }}>● {stats?.onTime || 0} on time</span>
            <span style={{ color: 'var(--danger)', fontWeight: 600 }}>● {stats?.overdue || 0} late</span>
            <span style={{ color: 'var(--text-muted)' }}>{stats?.submitted || 0} total submissions</span>
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Active reports */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>My Active Reports</h2>
            <button onClick={() => navigate('/reminders')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              View all <ChevronRight size={13} />
            </button>
          </div>
          {reminders.length === 0
            ? <EmptyState icon={Bell} title="No active reports" description="Reports assigned to you appear here." action={canManageReports && <Button icon={Plus} size="sm" onClick={() => navigate('/reminders/new')}>Create</Button>} />
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {reminders.map(r => (
                  <div key={r.id} onClick={() => navigate(`/reminders/${r.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: 'var(--bg-elevated)', borderRadius: 10, cursor: 'pointer', border: '1.5px solid transparent', transition: 'all .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--primary-ghost)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Bell size={14} color="var(--primary)" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.report_name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
                        Due: {r.next_due_date ? format(parseISO(r.next_due_date), 'MMM d, yyyy') : '—'} · {r.department_name || 'No dept'}
                      </div>
                    </div>
                    <FrequencyBadge frequency={r.frequency} />
                  </div>
                ))}
              </div>
            )}
        </Card>

        {/* Recent activity */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Recent Activity</h2>
            {canManageReports && <button onClick={() => navigate('/logs')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>View all <ChevronRight size={13} /></button>}
          </div>
          {recent.length === 0
            ? <EmptyState icon={Clock} title="No recent activity" description="Email logs and reminders appear here." />
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {recent.map((log, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, background: 'var(--bg-elevated)' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: log.status === 'sent' ? '#dcfce7' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {log.status === 'sent' ? <CheckCircle2 size={13} color="var(--success)" /> : <XCircle size={13} color="var(--danger)" />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.report_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{log.sent_at ? format(parseISO(log.sent_at), 'MMM d, h:mm a') : '—'}</div>
                    </div>
                    <Badge label={log.status} type={log.status === 'sent' ? 'success' : 'danger'} />
                  </div>
                ))}
              </div>
            )}
        </Card>
      </div>
    </div>
  );
}
